import { Button, Check, Clock, HourglassIcon, Popover, PopoverContent, PopoverTrigger, X } from '@chain-zen/ui'
import { useState } from 'react'

import { DriverAppDownloadMessageStatusEnum, type IDriverAppDownloadMessageAction } from '../../../types/tracking'
import { dateTimeAgo } from '../../../utils/datetime-utils'
import { normalizeAppDownloadActions } from './app-download-actions'

interface IAppDownloadStatusButtonProps {
  appDownloadActions?: IDriverAppDownloadMessageAction[]
  latestAction?: IDriverAppDownloadMessageAction | null
  reserveSpace?: boolean
}

/**
 * Format date for display in timeline
 */
const formatDate = (date: string | Date) => {
  const parsedDate = typeof date === 'string' ? new Date(date) : date
  return parsedDate.toLocaleDateString('en-US', {
    day: '2-digit',
    hour: 'numeric',
    hour12: true,
    minute: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

/**
 * Timeline component that displays the list of driver-app download actions in chronological order
 */
const AppDownloadActionsTimeline = ({
  actions
}: {
  actions?: IDriverAppDownloadMessageAction[] | Record<string, IDriverAppDownloadMessageAction>
}) => {
  if (!actions) return null

  const actionList: IDriverAppDownloadMessageAction[] = Array.isArray(actions) ? actions : Object.values(actions)

  if (actionList.length === 0) return null

  const normalizedActions = normalizeAppDownloadActions(actionList)

  return (
    <div className="space-y-3">
      <p className="text-sm font-semibold">App download message timeline</p>
      {normalizedActions.map((action) => {
        const { actionID, canceledAt, completedAt, messageDueDate, scheduledAt, sequence, status } = action
        const statusMeta =
          status === DriverAppDownloadMessageStatusEnum.Completed
            ? { icon: Check, iconClassName: 'text-green-500 dark:text-green-400', label: 'Sent' }
            : status === DriverAppDownloadMessageStatusEnum.Canceled
              ? { icon: X, iconClassName: 'text-red-500 dark:text-red-400', label: 'Cancelled' }
              : { icon: HourglassIcon, iconClassName: 'text-yellow-500 dark:text-yellow-400', label: 'Scheduled' }

        const StatusIcon = statusMeta.icon

        return (
          <div key={actionID || sequence} className="rounded-md border border-border bg-muted/30 p-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium">Message {sequence}</p>
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <StatusIcon className={`h-3 w-3 ${statusMeta.iconClassName}`} />
                {statusMeta.label}
              </span>
            </div>
            <div className="mt-2 space-y-1 pl-1 text-xs text-muted-foreground">
              {!!scheduledAt && <p>{`Scheduled ${formatDate(scheduledAt)} (${dateTimeAgo(scheduledAt)})`}</p>}
              {!!completedAt && <p>{`Sent ${formatDate(completedAt)} (${dateTimeAgo(completedAt)})`}</p>}
              {!!canceledAt && <p>{`Cancelled ${formatDate(canceledAt)} (${dateTimeAgo(canceledAt)})`}</p>}
              {!scheduledAt && !completedAt && !canceledAt && !!messageDueDate && (
                <p>{`Due ${formatDate(messageDueDate)} (${dateTimeAgo(messageDueDate)})`}</p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Button component that displays app download status with a clock icon
 * Shows a popover with the full timeline when clicked
 */
export const AppDownloadStatusButton = (props: IAppDownloadStatusButtonProps) => {
  const { appDownloadActions, latestAction, reserveSpace = false } = props
  const [isOpen, setIsOpen] = useState(false)

  // Don't show the button if there are no actions
  if (!appDownloadActions || appDownloadActions.length === 0) {
    return reserveSpace ? <span className="inline-flex h-7 w-7" aria-hidden={true} /> : null
  }

  const iconClassName = 'text-foreground'

  // Get status text for popover title
  const getStatusText = () => {
    if (!latestAction) return 'App download status'

    switch (latestAction.status) {
      case DriverAppDownloadMessageStatusEnum.Completed:
        return 'App download link sent'
      case DriverAppDownloadMessageStatusEnum.Canceled:
        return 'App download link cancelled'
      case DriverAppDownloadMessageStatusEnum.Scheduled:
        return 'App download link scheduled'
      default:
        return 'App download status'
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal={true}>
      <PopoverTrigger asChild={true}>
        <Button variant="outline" size="xs" className="h-7 w-7 p-0" aria-label="View app download timeline">
          <Clock className={`h-3 w-3 ${iconClassName}`} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="h-full max-h-[400px] w-80 max-w-lg overflow-y-auto p-3"
        side="bottom"
        align="start"
        alignOffset={10}
        sideOffset={10}
      >
        <div className="space-y-3">
          <p className="font-semibold text-sm">{getStatusText()}</p>
          <AppDownloadActionsTimeline actions={appDownloadActions} />
        </div>
      </PopoverContent>
    </Popover>
  )
}
